<template>
  <span v-if="(draft || target) && (tags.length || canEdit || failed)" ref="root" class="person-tags" :class="{'person-tags--static':!interactive,'person-tags--compact':compact}" @click="interactive && $event.stopPropagation()" @keydown.enter="interactive && $event.stopPropagation()" @keydown.space="interactive && $event.stopPropagation()">
    <template v-for="tag in shown" :key="tag.id">
      <button v-if="interactive" type="button" class="person-tags__chip" :style="tagStyle(tag)" v-tooltip.top="{value:tooltip(tag),showDelay:250,escape:true}" :aria-label="`${tag.name}. View tags for ${name || person?.name || 'this person'}`" @click="open(tag.id)"><i v-if="tag.visibility!=='all'" class="pi pi-lock" aria-hidden="true" /><span>{{ tag.name }}</span></button>
      <span v-else class="person-tags__chip" :style="tagStyle(tag)" :title="tooltip(tag)"><i v-if="tag.visibility!=='all'" class="pi pi-lock" aria-hidden="true" /><span>{{ tag.name }}</span></span>
    </template>
    <template v-if="remaining.length">
      <button v-if="interactive" type="button" class="person-tags__more" :aria-label="`Show ${remaining.length} more tags`" v-tooltip.top="{value:remainingLabel,showDelay:250,escape:true}" @click="open()">+{{ remaining.length }}</button>
      <span v-else class="person-tags__more" :title="remainingLabel">+{{ remaining.length }}</span>
    </template>
    <button v-if="canEdit" type="button" class="person-tags__add" :class="{'person-tags__add--icon':compact && tags.length}" aria-label="Add or remove tags" v-tooltip.top="'Add or remove tags'" @click="open()"><i class="pi pi-tags" aria-hidden="true" /><span v-if="!compact || !tags.length">Add tag</span></button>
    <button v-if="failed && interactive" type="button" class="person-tags__retry" @click="retry"><i class="pi pi-refresh" aria-hidden="true" /><span>Retry tags</span></button>
  </span>
</template>
<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { personTagTarget,tagTextColor } from '../utils/peopleTagUtils';
import { usePersonTags,openPeopleTags,openDraftPeopleTags,queuePersonTags,fetchPeopleTags,peopleTagsState as state } from '../composables/usePeopleTags';
const props=defineProps({candidateId:[Number,String],employeeId:[Number,String],userId:[Number,String],name:{type:String,default:''},limit:{type:Number},editable:{type:Boolean,default:false},interactive:{type:Boolean,default:true},compact:{type:Boolean,default:false},draft:{type:Boolean,default:false},kind:{type:String,default:'employee'},modelValue:{type:Array,default:()=>[]},disabled:{type:Boolean,default:false}});
const emit=defineEmits(['update:modelValue']);
const target=computed(()=>props.draft ? null : personTagTarget(props));
const {person,tags:assignedTags}=usePersonTags(target);
const catalogFailed=ref(false);
const tags=computed(()=>props.draft ? state.tags.filter(tag=>tag.is_active && props.modelValue.includes(tag.id) && (tag.applies_to==='both' || tag.applies_to===props.kind)) : assignedTags.value);
const root=ref(null);
const fitted=ref(null);
let fitGeneration=0;
let fitting=false;
let fitAgain=false;
let lastWidth=0;
let resizeObserver=null;
const visibleLimit=computed(()=>props.limit===undefined?props.compact?2:tags.value.length:Math.max(0,Math.floor(props.limit)));
const shownCount=computed(()=>fitted.value==null?visibleLimit.value:Math.max(0,Math.min(visibleLimit.value,fitted.value)));
const shown=computed(()=>tags.value.slice(0,shownCount.value));
const remaining=computed(()=>tags.value.slice(shownCount.value));
const remainingLabel=computed(()=>remaining.value.map(tag=>tag.name).join('\n'));
const canEdit=computed(()=>props.draft ? props.interactive && !props.disabled && state.available && !!state.capabilities.assign : props.interactive && props.editable && person.value?.can_assign);
const failed=computed(()=>props.draft ? catalogFailed.value : props.editable && !person.value && !!state.error);
const tooltip=tag=>[tag.name,tag.description].filter(Boolean).join('\n');
const tagStyle=tag=>({backgroundColor:tag.color,color:tagTextColor(tag.color)});
const open=focusTagId=>{
  if(props.disabled) return;
  if(props.draft){ openDraftPeopleTags({kind:props.kind,tagIds:props.modelValue,name:props.name,focusTagId,onCommit:ids=>emit('update:modelValue',ids)}); return; }
  openPeopleTags({targets:[target.value],name:props.name || person.value?.name || '',focusTagId});
};
async function ensureCatalog(){
  catalogFailed.value=false;
  try { await fetchPeopleTags({action:'catalog'}); }
  catch { catalogFailed.value=true; }
}
const retry=()=>props.draft ? ensureCatalog() : queuePersonTags(target.value,true);
function scheduleFit(){
  if(fitting){ fitAgain=true; return; }
  void fitToLine();
}
async function fitToLine(){
  const generation=++fitGeneration;
  if(props.compact){ fitted.value=null; return; }
  const cap=visibleLimit.value;
  if(!root.value){ fitted.value=cap; return; }
  fitting=true;
  try{
    let count=fitted.value==null?cap:Math.max(0,Math.min(cap,fitted.value));
    fitted.value=count;
    await nextTick();
    const el=root.value;
    if(generation!==fitGeneration || !el || el.clientWidth<8) return;
    if(el.clientWidth===lastWidth && el.scrollWidth<=el.clientWidth+1) return;
    while(count>0 && el.scrollWidth>el.clientWidth+1){
      count-=1;
      fitted.value=count;
      await nextTick();
      if(generation!==fitGeneration || !root.value) return;
    }
    while(count<cap){
      fitted.value=count+1;
      await nextTick();
      if(generation!==fitGeneration || !root.value) return;
      if(root.value.scrollWidth>root.value.clientWidth+1){
        fitted.value=count;
        return;
      }
      count+=1;
    }
    lastWidth=root.value?root.value.clientWidth:0;
  }finally{
    fitting=false;
    if(fitAgain && generation===fitGeneration){ fitAgain=false; scheduleFit(); }
  }
}
onMounted(()=>{
  if(props.draft && !state.loaded) ensureCatalog();
  scheduleFit();
  if(typeof ResizeObserver==='undefined' || !root.value) return;
  resizeObserver=new ResizeObserver(()=>scheduleFit());
  resizeObserver.observe(root.value);
});
onBeforeUnmount(()=>resizeObserver?.disconnect());
watch(()=>[tags.value.map(tag=>tag.id).join(),canEdit.value,props.compact,props.limit],()=>{ fitted.value=null; scheduleFit(); });
</script>
<style scoped>
.person-tags { display:flex; flex-wrap:nowrap; align-items:center; gap:6px; width:100%; min-width:0; max-width:100%; margin-top:6px; overflow:hidden; vertical-align:middle; }
.person-tags__chip,.person-tags__more,.person-tags__add,.person-tags__retry { appearance:none; display:inline-flex; align-items:center; justify-content:center; gap:5px; min-height:26px; min-width:0; border:1px solid transparent; border-radius:6px; padding:4px 8px; font-family:inherit; font-size:12px; font-weight:600; line-height:1.35; cursor:pointer; max-width:190px; text-align:left; text-decoration:none; }
.person-tags__chip span { min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.person-tags__chip .pi { font-size:10px; flex-shrink:0; }
.person-tags__more { flex-shrink:0; background:var(--crc-content-bg,var(--surface-100,#f1f5f9)); color:var(--crc-text-strong,var(--text-color)); border-color:var(--crc-panel-border,var(--surface-border)); }
.person-tags__add,.person-tags__retry { flex-shrink:0; background:transparent; color:var(--crc-text-muted,#64748b); border:1px dashed var(--crc-panel-border,var(--surface-border,#cbd5e1)); font-weight:500; }
.person-tags__add:hover { color:var(--crc-green,#16a34a); border-color:currentColor; background:color-mix(in srgb,var(--crc-green,#16a34a) 6%,transparent); }
.person-tags button:focus-visible { outline:2px solid var(--crc-green,#16a34a); outline-offset:3px; }
.person-tags__chip:hover { filter:brightness(.94); }
.person-tags--static .person-tags__chip,.person-tags--static .person-tags__more { cursor:inherit; }
.person-tags--compact { display:flex; flex-wrap:nowrap; gap:4px; width:100%; margin:0 0 4px; overflow:visible; }
.person-tags--compact .person-tags__chip { flex:0 1 auto; max-width:136px; justify-content:flex-start; }
.person-tags--compact .person-tags__chip,.person-tags--compact .person-tags__more,.person-tags--compact .person-tags__add,.person-tags--compact .person-tags__retry { min-height:24px; padding:3px 6px; font-size:11px; line-height:1.3; border-radius:5px; }
.person-tags--compact .person-tags__add--icon { width:24px; min-width:24px; padding:0; border-style:solid; }
.person-tags--compact .person-tags__add .pi { font-size:12px; }
@media(pointer:coarse) { .person-tags button { min-height:36px; } .person-tags--compact .person-tags__add--icon { width:36px; min-width:36px; } }
</style>
